import type { Table } from 'apache-arrow'
import { tableFromIPC } from 'apache-arrow'
import type { Observable } from 'rxjs'
import { map } from 'rxjs'
import type { AjaxResponse } from 'rxjs/ajax'
import { Environment } from 'environment.js'
import { tracedAjax } from '../Sentry.js'
import type { BlastSource } from '../../Model/Project/BlastSource.js'
import type { SpinSelection } from './CubeKey.js'

export const CUBE_ARROW_MIME = 'application/vnd.apache.arrow.stream'

/**
 * Fresh cube response — server sent a new record batch.
 *
 * `etag` may be `null` if the backend couldn't compute one (e.g. an unrefreshed project
 * before any derived data exists). Callers should pass it back unchanged to `fetchCube` for
 * future revalidation; if it's `null`, every request will be a fresh fetch.
 */
export type CubeFresh = {
  kind: 'fresh'
  table: Table
  /**
   * The raw IPC bytes the table was parsed from. Kept so callers can persist the cube to
   * IndexedDB without re-serialising — Arrow Java/JS would round-trip cleanly but it's
   * cheaper and exactly bit-faithful to pass through what the server actually sent.
   */
  bytes: Uint8Array
  etag: string | null
}

/** 304 response — the locally-known cube is still current. */
export type CubeUnchanged = {
  kind: 'unchanged'
}

export type CubeFetchResult = CubeFresh | CubeUnchanged

/**
 * Fetch the visualization cube for a (scopeId, blastType[, spin]) tuple from the backend.
 *
 * When [spin] is provided the cube is sourced from the SPIN endpoint
 * (`/spin/{scopeId}/cube?blastType&algorithmType&atTime`) instead of the OTU endpoint
 * (`/krona/{scopeId}/cube?blastType`); both return the same Arrow shape. An empty
 * `spin.atTime` is omitted so the backend applies its "latest / now()" default.
 *
 * Always negotiates Apache Arrow IPC — the legacy JSON encoding on this endpoint is kept
 * server-side only for curl/ops debugging. If [currentEtag] is provided, sends `If-None-Match`
 * so the backend can short-circuit with `304 Not Modified`.
 *
 */
export const fetchCube = (
  scopeId: string,
  blastType: BlastSource,
  currentEtag?: string | null,
  spin?: SpinSelection,
): Observable<CubeFetchResult> => fetchArrowCube(cubeUrl(scopeId, blastType, spin), currentEtag)

/** An Arrow IPC cube at [url], sending [currentEtag] for a `304 Not Modified`. */
export const fetchArrowCube = (url: string, currentEtag?: string | null): Observable<CubeFetchResult> => {
  const headers: Record<string, string> = { Accept: CUBE_ARROW_MIME }
  if (currentEtag) {
    headers['If-None-Match'] = currentEtag
  }

  return tracedAjax<ArrayBuffer>({
    url,
    method: 'GET',
    withCredentials: true,
    responseType: 'arraybuffer',
    headers,
  }).pipe(map((response) => parseResponse(response)))
}

const cubeUrl = (scopeId: string, blastType: BlastSource, spin?: SpinSelection): string => {
  if (!spin) {
    return `${Environment.VIS_URL}/krona/${scopeId}/cube?blastType=${blastType}`
  }
  const params = new URLSearchParams({ blastType, algorithmType: spin.algorithmType })
  if (spin.atTime) params.set('atTime', spin.atTime)
  return `${Environment.VIS_URL}/spin/${scopeId}/cube?${params.toString()}`
}

const parseResponse = (response: AjaxResponse<ArrayBuffer>): CubeFetchResult => {
  if (response.status === 304) {
    return { kind: 'unchanged' }
  }

  const body = response.response
  if (!body || body.byteLength === 0) {
    throw new Error(`Cube fetch returned ${response.status} with empty body — expected Arrow IPC stream`)
  }

  const bytes = new Uint8Array(body)
  const table = tableFromIPC(bytes)
  return { kind: 'fresh', table, bytes, etag: response.xhr?.getResponseHeader('ETag') ?? null }
}
